import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { fetchSession, buildApiUrl } from "../lib/api-client";

export interface AuthUser {
    userId: string;
    email: string;
    roles: string[];
    teamIds: string[];
    memberships: Array<{
        teamId: string;
        role: string;
        roles: string[];
        teamSlug: string | null;
        teamName: string | null;
    }>;
    teamRoles: Record<string, string[]>;
}

export interface AuthContextValue {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: Error | null;
    refreshSession: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        // Instead of throwing, return a safe default that allows components to function
        // This prevents blank pages when AuthProvider fails or is unavailable
        console.warn("useAuth called outside AuthProvider - returning unauthenticated state");
        return {
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: new Error("Auth context unavailable"),
            refreshSession: async () => {
                console.warn("refreshSession called but AuthProvider is unavailable");
            },
            logout: async () => {
                console.warn("logout called but AuthProvider is unavailable");
            },
        };
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const refreshSession = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await fetchSession();

            if (response.session) {
                setUser({
                    userId: response.session.userId,
                    email: response.session.email,
                    roles: response.session.roles,
                    teamIds: response.session.teamIds,
                    memberships: response.session.memberships,
                    teamRoles: response.session.teamRoles || {},
                });
            } else {
                setUser(null);
            }
        } catch (err) {
            // Log as warning instead of error to prevent breaking the app
            // Authentication failures should not prevent the app from functioning
            console.warn("Session fetch failed (non-critical):", err);
            setError(err instanceof Error ? err : new Error("Failed to fetch session"));
            // Set user to null to indicate unauthenticated state
            // This allows the app to continue functioning without auth
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch(buildApiUrl("/auth/logout"), {
                method: "POST",
                credentials: "include",
            });
            setUser(null);
        } catch (err) {
            console.error("Logout failed:", err);
        }
    }, []);

    useEffect(() => {
        void refreshSession();
    }, [refreshSession]);

    const value: AuthContextValue = {
        user,
        isAuthenticated: user !== null,
        isLoading,
        error,
        refreshSession,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useRequireAuth(): AuthUser {
    const { user, isAuthenticated } = useAuth();

    if (!isAuthenticated || !user) {
        throw new Error("Authentication required");
    }

    return user;
}

export function useTeamRole(teamId: string): string[] {
    const { user } = useAuth();
    return user?.teamRoles[teamId] ?? [];
}

export function useHasRole(role: string, teamId?: string): boolean {
    const { user } = useAuth();

    if (!user) return false;

    if (teamId) {
        return user.teamRoles[teamId]?.includes(role) ?? false;
    }

    return user.roles.includes(role);
}

export function useHasAnyRole(roles: string[], teamId?: string): boolean {
    const { user } = useAuth();

    if (!user) return false;

    if (teamId) {
        const teamRoles = user.teamRoles[teamId] ?? [];
        return roles.some((role) => teamRoles.includes(role));
    }

    return roles.some((role) => user.roles.includes(role));
}
